import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface ClassTemplate {
  id: number;
  weekday: number;
  weekday_name: string;
  start_time: string | null;
  end_time: string | null;
  subject: string | null;
  location: string | null;
  is_active: boolean;
  teacher_code: string | null;
}

export interface LearningReport {
  teaching_date: string;
  subject: string;
  teacher_id: string;
  teaching_type: string | null;
  teaching_location: string;
  learning_status: string;
  teaching_duration: string;
}

interface UseStudentDepletionDetailResult {
  classTemplates: ClassTemplate[];
  learningReports: LearningReport[];
  isLoading: boolean;
  error: string | null;
}

export function useStudentDepletionDetail(studentCode: string | null): UseStudentDepletionDetailResult {
  const [classTemplates, setClassTemplates] = useState<ClassTemplate[]>([]);
  const [learningReports, setLearningReports] = useState<LearningReport[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!studentCode) {
      setClassTemplates([]);
      setLearningReports([]);
      setError(null);
      return;
    }

    let cancelled = false;

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const { data, error: fnError } = await supabase.functions.invoke("get-student-depletion-detail", {
          body: { studentCode },
        });

        if (cancelled) return;

        if (fnError) {
          setError(fnError.message);
          return;
        }

        setClassTemplates(data?.class_templates ?? []);
        setLearningReports(data?.learning_reports ?? []);
      } catch (err) {
        if (!cancelled) {
          setError("เกิดข้อผิดพลาดในการดึงข้อมูล");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchData();

    return () => { cancelled = true; };
  }, [studentCode]);

  return { classTemplates, learningReports, isLoading, error };
}
